import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoTableAction,
  PoTableColumn,
} from '@po-ui/ng-components';
import { Project, ProjectFilters, UpdateProjectInput } from '../../interfaces/project';
import { SonarExtractorService } from '../../services/sonar-extractor.service';

@Component({
  selector: 'app-sonar-extractor-projects',
  templateUrl: './sonar-extractor-projects.component.html',
  styleUrls: ['./sonar-extractor-projects.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SonarExtractorProjectsComponent implements OnInit {
  @ViewChild('modalEdit', { static: true }) modalEdit!: PoModalComponent;

  // ── Tabela de Projetos ──────────────────────────────────────────────────────
  projects: Project[] = [];
  loading  = false;
  currentPage  = 1;
  readonly pageLimit = 50;
  hasMoreProjects = false;

  // ── Filtros ─────────────────────────────────────────────────────────────────
  filterName = '';

  // ── Formulário de Edição ────────────────────────────────────────────────────
  selectedProject: Project | null = null;
  formArea    = '';
  formModule  = '';
  formPo      = '';
  formSegment = '';
  formSquad   = '';
  formTribe   = '';
  formLoading = false;

  // ── Colunas ─────────────────────────────────────────────────────────────────
  readonly projectsColumns: PoTableColumn[] = [
    { property: 'sonar_key', label: 'Chave Sonar', type: 'string', width: '200px' },
    { property: 'name',      label: 'Nome',        type: 'string' },
    { property: 'area',      label: 'Área',        type: 'string' },
    { property: 'module',    label: 'Módulo',      type: 'string' },
    { property: 'po',        label: 'PO',          type: 'string' },
    { property: 'segment',   label: 'Segmento',    type: 'string' },
    { property: 'squad',     label: 'Squad',       type: 'string' },
    { property: 'tribe',     label: 'Tribe',       type: 'string' },
  ];

  readonly projectsActions: PoTableAction[] = [
    {
      label:  'Editar',
      icon:   'po-icon-edit',
      action: (project: Project) => this.openEditModal(project),
    },
  ];

  // ── Ações do Modal ──────────────────────────────────────────────────────────
  modalEditPrimaryAction: PoModalAction = {
    label:   'Salvar',
    loading: false,
    action:  () => this.saveProject(),
  };

  modalEditSecondaryAction: PoModalAction = {
    label:  'Cancelar',
    action: () => this.modalEdit.close(),
  };

  constructor(
    private sonarService: SonarExtractorService,
    private notification: PoNotificationService,
    private destroyRef:   DestroyRef,
    private cdr:          ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.loadProjects());
  }

  // ── Carregamento ────────────────────────────────────────────────────────────

  loadProjects(append = false): void {
    this.loading = true;

    const filters: ProjectFilters = {
      page:  this.currentPage,
      limit: this.pageLimit,
    };

    if (this.filterName.trim()) {
      filters.name = this.filterName.trim();
    }

    this.sonarService
      .getProjects(filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.projects       = append ? [...this.projects, ...result] : result;
          this.hasMoreProjects = result.length === this.pageLimit;
          this.loading        = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.notification.error('Erro ao carregar os projetos.');
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadProjects();
  }

  onClearSearch(): void {
    this.filterName  = '';
    this.currentPage = 1;
    this.loadProjects();
  }

  loadMoreProjects(): void {
    this.currentPage++;
    this.loadProjects(true);
  }

  // ── Formulário de Edição ────────────────────────────────────────────────────

  openEditModal(project: Project): void {
    this.selectedProject = project;
    this.formArea        = project.area    ?? '';
    this.formModule      = project.module  ?? '';
    this.formPo          = project.po      ?? '';
    this.formSegment     = project.segment ?? '';
    this.formSquad       = project.squad   ?? '';
    this.formTribe       = project.tribe   ?? '';
    this.modalEdit.open();
  }

  saveProject(): void {
    if (!this.selectedProject) return;

    this.formLoading                    = true;
    this.modalEditPrimaryAction.loading = true;

    const input: UpdateProjectInput = {
      area:    this.formArea.trim()    || undefined,
      module:  this.formModule.trim()  || undefined,
      po:      this.formPo.trim()      || undefined,
      segment: this.formSegment.trim() || undefined,
      squad:   this.formSquad.trim()   || undefined,
      tribe:   this.formTribe.trim()   || undefined,
    };

    this.sonarService
      .updateProject(this.selectedProject.sonar_key, input)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.formLoading                    = false;
          this.modalEditPrimaryAction.loading = false;
          this.modalEdit.close();
          this.notification.success('Projeto atualizado com sucesso.');

          // Atualiza o item na tabela localmente sem recarregar tudo
          this.projects = this.projects.map((p) =>
            p.sonar_key === updated.sonar_key ? updated : p
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.formLoading                    = false;
          this.modalEditPrimaryAction.loading = false;
          this.notification.error('Erro ao atualizar o projeto.');
          this.cdr.markForCheck();
        },
      });
  }
}

